import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';

import { AppRouteService } from '@services/ui/app-route.service';
import { UserService } from '@services/user/user.service';
import { routerLink } from '@models/constants';
import { UserData, Role } from '@models/User/UserData';
import { AppLink } from '@models/app-link-model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private userService: UserService, 
    private appRouteService: AppRouteService,
    private router: Router
  ) {}

  // --- METODI SINCRONI (Stesse signature) ---

  private isUserWithRole(userdata: UserData, role: Role): boolean {
    return userdata && userdata.roles.some(r => r.toUpperCase() === role.toUpperCase());
  }

  public isUserAdvanced(userdata: UserData): boolean {
    return this.isUserWithRole(userdata, Role.ADVANCED);
  }

  public isUserAdmin(userdata: UserData): boolean {
    return this.isUserWithRole(userdata, Role.ADMIN);
  }

  public isTenantEnterprise(userdata: UserData): boolean {
    return userdata.tenantProfile === 'ENTERPRISE';
  }

  public isUserGuest(userdata: UserData): boolean {
    return this.isUserWithRole(userdata, Role.GUEST);
  }

  // --- METODI REATTIVI (Stesse signature) ---

  private isWithRole(role: Role): Observable<boolean> {
    return this.userService.getUserdata().pipe(
      map((userdata: UserData) => this.isUserWithRole(userdata, role)),
      catchError((err: any) => {
        console.error('Errore nel recupero dati utente', err);
        return of(false);
      }),
    );
  }

  public isAdvanced(): Observable<boolean> {
    return this.isWithRole(Role.ADVANCED);
  }

  public isGuest(): Observable<boolean> {
    return this.isWithRole(Role.GUEST);
  }

  public canActivateUrl(url: string): Observable<boolean> {
    // Nota: canActivateUrl ora usa internamente la logica ottimizzata
    return this.userDataContainsView(url, true);
  }

  public userDataContainsView(url: string, redirect?: boolean): Observable<boolean> {
    const urlToValidate = url.split('?')[0];

    return this.userService.getUserdata().pipe(
      take(1), // Impedisce alla Guard di rimanere appesa
      map((userdata: UserData) => {
        const appLink = routerLink.find((rl: AppLink) => 
          (rl.link && rl.link === urlToValidate) || 
          (rl.regex && rl.regex.test(urlToValidate))
        );
        
        const viewName = appLink?.viewName;
        const hasAccess = viewName ? userdata.views.includes(viewName) : false;

        if (redirect && !hasAccess) {
          this.handleUnauthorizedRedirect(userdata, urlToValidate);
        }

        return hasAccess;
      }),
      catchError((err) => {
        console.error('Errore nel controllo permessi:', err);
        return of(false);
      })
    );
  }

  public canActivateView(viewName: string, redirect?: boolean): Observable<boolean> {
    return this.userService.getUserdata().pipe(
      take(1),
      map((userdata: UserData) => {     
        return this.canUserActivateView(userdata, viewName, redirect);   
      }),
      catchError((err: any) => {
        console.error('Errore nel recupero dati utente', err);
        return of(false);
      }),
    );       
  }  

  public canUserActivateView(userdata: UserData, viewName: string, redirect?: boolean): boolean {
    const hasAccess = viewName ? userdata.views.includes(viewName) : false;

    if (redirect && !hasAccess) {
      this.handleUnauthorizedRedirect(userdata, "View: " + viewName);
    }
    
    return hasAccess;
  }

  // --- LOGICA DI SUPPORTO (Privata) ---

  private handleUnauthorizedRedirect(userdata: UserData, context: string): void {
    console.error("Accesso negato o URL non mappato:", context);
    
    // Priorità 1: Home
    if (userdata.views.includes('home')) {
      this.appRouteService.goToSignAppHome();
      return;
    } 
    
    // Priorità 2: Prima vista disponibile tramite routerLink mapping
    const firstAvailableViewName = userdata.views[0];
    const firstAvailableLink = routerLink.find((rl: AppLink) => 
      rl.viewName === firstAvailableViewName || rl.title === firstAvailableViewName
    );

    if (firstAvailableLink?.link) {
      this.appRouteService.goTo(firstAvailableLink.link);
    }
  }
}